import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';

// Set REACT_APP_GOOGLE_CLIENT_ID (the OAuth Web client ID) to enable Google
// sign-in. When it's empty the button renders nothing, so the app works fine
// without it configured. Note: CRA bakes env vars in at build time, so the
// frontend must be rebuilt after this is set.
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

declare global {
  interface Window {
    google?: any;
  }
}

// Load Google Identity Services once, sharing the promise across button mounts.
let gisPromise: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google sign-in'));
    document.head.appendChild(s);
  });
  return gisPromise;
}

interface Props {
  /** "signin_with" | "signup_with" | "continue_with" — Google button label. */
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  onError?: (message: string) => void;
}

const GoogleSignInButton: React.FC<Props> = ({ text = 'continue_with', onError }) => {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();

  const handleCredential = useCallback(
    async (response: any) => {
      try {
        await loginWithGoogle(response.credential);
        trackEvent(text === 'signup_with' ? 'sign_up' : 'login', { method: 'google' });
        navigate('/');
      } catch (e: any) {
        onError?.(e?.response?.data?.detail || 'Google sign-in failed. Please try again.');
      }
    },
    [loginWithGoogle, navigate, onError, text]
  );

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    loadGis()
      .then(() => {
        if (cancelled || !ref.current || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential,
        });
        window.google.accounts.id.renderButton(ref.current, {
          theme: 'filled_black',
          size: 'large',
          type: 'standard',
          text,
          shape: 'pill',
          logo_alignment: 'center',
          width: 300,
        });
      })
      .catch(() => onError?.('Could not load Google sign-in.'));
    return () => {
      cancelled = true;
    };
  }, [handleCredential, text, onError]);

  if (!GOOGLE_CLIENT_ID) return null;
  return <div className="flex justify-center" ref={ref} />;
};

export default GoogleSignInButton;
